/**
 * Fusion (Melco) <-> Dawn 15 variant bridge.
 *
 * TEMPORARY WORKAROUND — delete this file (and its section) once Melco ships
 * Dawn 15 support. This is Melco's bug, not a theme bug.
 *
 * Fusion's melco-fusion.js gates BOTH of its variant listeners behind
 * `customElements.whenDefined("variant-radios")`. Dawn 15 removed the
 * `variant-radios` custom element (consolidated into `variant-selects`), so that
 * promise never resolves and neither listener is ever attached. Consequences:
 *
 *   1. The Personalize button's `data-product-id` stays frozen on the default
 *      variant. Fusion's add-to-cart guard looks the button up by the LIVE
 *      variant id, finds nothing, and returns early — so "personalization
 *      required" silently stops being enforced on every non-default variant.
 *   2. The colour-change guard never fires, so a `_melco_session` from one
 *      colour rides along to another (wrong personalization on the order).
 *
 * Fusion is additionally incompatible in two more ways, handled below:
 *   - it reads `variant.currentVariant.id`, but Dawn 15's variant-selects has
 *     no `currentVariant` property at all;
 *   - its `isColorOption` compares against `name === "Color"`, but Dawn 15 names
 *     radios `"Color-1"` (option name + position).
 *
 * This file re-implements the two dead listeners against Dawn 15's pub/sub API,
 * mirroring Fusion's own side effects exactly — downstream Fusion code depends
 * on these precise mutations, so do not "improve" them here.
 */
(function () {
  'use strict';

  var BUTTON_SELECTOR = '.melco-fusion-personalization-button';
  var PRODUCT_SELECTOR = '.melco-fusion-personalization-product';

  // If `variant-radios` exists, Fusion's own listeners attach and this bridge
  // must stay out of the way (avoids double confirm() dialogs).
  if (window.customElements && customElements.get('variant-radios')) return;

  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  function removePropertiesField(propertyName) {
    var fields = document.querySelectorAll('input[name="properties[' + propertyName + ']"]');
    Array.prototype.forEach.call(fields, function (field) {
      field.remove();
    });
  }

  // Mirror of Fusion's resetMelcoProperties().
  function resetMelcoProperties() {
    var previewPropertiesField = document.querySelector('[name="properties[_melco_preview_properties]"]');
    if (!previewPropertiesField) return;

    try {
      JSON.parse(previewPropertiesField.value || '[]').forEach(function (previewProperty) {
        removePropertiesField(previewProperty);
      });
    } catch (e) {
      console.error(e);
    }

    removePropertiesField('_melco_session');
    removePropertiesField('_melco_preview_properties');
  }

  function isCurrentlyPersonalized() {
    return !!document.querySelector('input[name="properties[_melco_session]"]');
  }

  function currentVariantId() {
    var input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    return input && input.value;
  }

  // Fusion keys every lookup off data-product-id, so both the button and any
  // personalization-product elements have to track the selected variant.
  function syncProductId(variantId) {
    if (!variantId) return;

    var button = document.querySelector(BUTTON_SELECTOR);
    if (button) button.dataset.productId = variantId;

    var products = document.querySelectorAll(PRODUCT_SELECTOR);
    Array.prototype.forEach.call(products, function (product) {
      product.dataset.productId = variantId;
    });
  }

  // (1) Replaces Fusion's dead product-id sync.
  // variantChange fires after the variant resolves and carries the new variant;
  // optionValueSelectionChange would fire too early to read a settled id.
  subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
    var variant = event && event.data && event.data.variant;
    syncProductId((variant && variant.id) || currentVariantId());
  });

  // (2) Replaces Fusion's dead colour-change guard. Delegated from the document
  // so it survives Dawn re-rendering the product form; capture phase so it runs
  // before Dawn's own change handling.
  document.addEventListener(
    'click',
    function (event) {
      var input = event.target;
      if (!input || input.type !== 'radio') return;
      if (!input.closest('variant-selects, variant-radios, .product-form__input')) return;

      var button = document.querySelector(BUTTON_SELECTOR);
      if (!button) return;

      var colorOptionName = button.dataset.colorOptionName;
      if (!colorOptionName) return;

      // Dawn 15 names radios "<Option>-<position>"; Fusion expects "<Option>".
      var name = (input.name || '').trim();
      if (name !== colorOptionName && name.indexOf(colorOptionName + '-') !== 0) return;

      if (!isCurrentlyPersonalized()) return;

      if (window.confirm(button.dataset.warningHint)) {
        resetMelcoProperties();
      } else {
        event.preventDefault();
      }
    },
    true
  );

  // The app block renders server-side with the default variant, so align once on
  // load for deep links like ?variant=<non-default>.
  function syncOnLoad() {
    syncProductId(currentVariantId());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncOnLoad);
  } else {
    syncOnLoad();
  }
})();
